/**
 * LDS order-page financial SSOT — passthrough from Backend blocks, with a
 * targeted handling-fee guard when display_total equals priceWithCommission
 * (+ baggage) and skips the prepared agent commission.
 *
 * Sources (priority):
 *   1. order.lds_partial_payment_display.{display_total, display_paid, display_remaining}
 *   2. order.lds_updated_order_total.display_total_updated
 */
import { extractLdsOrderFinalPriceIls } from "../extractLdsOrderFinalPriceIls.js";
import { resolveLdsConfirmedPaidSumFromPayments } from "./ldsOrderPaymentSummarySsot.js";
import { resolveCampaignOrderFinancialOverlay } from "./resolveCampaignOrderFinancialSsot.js";
import { applyLdsMissingHandlingFeeToPayableTotal } from "./ldsMissingHandlingFeePayableUplift.js";

export const LDS_FE_FINAL_FINANCIAL_SSOT_TRACE_TAG =
  "[LDS_FE_FINAL_FINANCIAL_SSOT_TRACE]";

export const LDS_BACKEND_FINANCIAL_SSOT_TRACE_TAG =
  "[LDS_BACKEND_FINANCIAL_SSOT_TRACE]";

const MONEY_TOL = 0.02;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function positive(value) {
  const n = toNumberOrNull(value);
  return n != null && n > 0 ? n : null;
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

/** Legacy fields — trace only; never used as display total. */
export function collectRejectedFinancialCandidates(order) {
  if (!order || typeof order !== "object") return [];
  const op = order?.ourprice ?? order?.ourPrice ?? {};
  const snap = op?.pricingSnapshot ?? op?.pricing_snapshot ?? {};
  const rejected = [];
  const add = (source, value) => {
    const n = toNumberOrNull(value);
    if (n == null) return;
    rejected.push({ source, value: round2(n) });
  };

  add("order.customer_total", order?.customer_total ?? order?.customerTotal);
  add("ourprice.priceWithBaggageFare", op?.priceWithBaggageFare);
  add("order.display_total_ils", order?.display_total_ils);
  add(
    "order.resolved_final_total_ils",
    order?.resolved_final_total_ils ?? order?.resolvedFinalTotalIls
  );
  add(
    "order.resolved_final_total",
    order?.resolved_final_total ?? order?.resolvedFinalTotal
  );
  add("ourprice.priceWithCreditCardFee", op?.priceWithCreditCardFee);
  add(
    "ourprice.priceWithCommissionAndCreditCardFee",
    op?.priceWithCommissionAndCreditCardFee ??
      op?.ourTotalFare?.priceWithCommissionAndCreditCardFee
  );
  add("pricingSnapshot.final_price", snap?.final_price ?? snap?.finalPrice);
  add("extractLdsOrderFinalPriceIls", extractLdsOrderFinalPriceIls(order));
  add("order.admin_total_amount", order?.admin_total_amount ?? order?.adminTotalAmount);
  add("legacy.sumpaid", order?.sumpaid);

  return rejected;
}

/**
 * @param {object|null|undefined} order
 * @param {{ totalAmountOverride?: number|null, emitTrace?: boolean }} [options]
 */
export function resolveLdsBackendFinancialSsot(order, options = {}) {
  const empty = {
    total: null,
    paid: 0,
    remaining: null,
    totalSource: "none",
    paidSource: "none",
    remainingSource: "none",
    hasBackendBlock: false,
    rejectedCandidates: [],
  };

  if (!order || typeof order !== "object") {
    return empty;
  }

  const partial = order.lds_partial_payment_display ?? order.ldsPartialPaymentDisplay;
  const updated = order.lds_updated_order_total ?? order.ldsUpdatedOrderTotal;
  const rejectedCandidates = collectRejectedFinancialCandidates(order);

  let total = null;
  let totalSource = "none";
  let paid = 0;
  let paidSource = "none";
  let remaining = null;
  let remainingSource = "none";

  if (partial && typeof partial === "object") {
    if (partial.display_total != null) {
      total = round2(Number(partial.display_total));
      totalSource = "lds_partial_payment_display.display_total";
    }
    if (partial.display_paid != null || partial.display_paid_actual != null) {
      paid = round2(Number(partial.display_paid_actual ?? partial.display_paid));
      paidSource = "lds_partial_payment_display.display_paid";
    }
    if (partial.display_remaining != null) {
      remaining = round2(Number(partial.display_remaining));
      remainingSource = "lds_partial_payment_display.display_remaining";
    }
  }

  if (total == null && updated?.display_total_updated != null) {
    total = round2(Number(updated.display_total_updated));
    totalSource = String(
      updated.source_used || "lds_updated_order_total.display_total_updated"
    );
  }

  const override = toNumberOrNull(options?.totalAmountOverride);
  if (override != null && override >= 0) {
    total = round2(override);
    totalSource = "options.totalAmountOverride";
  }

  if (paid <= MONEY_TOL) {
    const paidPick = resolveLdsConfirmedPaidSumFromPayments(order);
    paid = paidPick.value ?? 0;
    paidSource = paidPick.source;
  }

  if (remaining == null && total != null) {
    remaining = round2(Math.max(total - paid, 0));
    remainingSource = "derived:backend_total_minus_confirmed_paid";
  }

  const campaignOverlay = resolveCampaignOrderFinancialOverlay(order);
  if (campaignOverlay) {
    const ldsTotalMissing = total == null || total <= MONEY_TOL;
    const ldsPaidMissing = paid <= MONEY_TOL;
    const campaignHasTotal =
      campaignOverlay.total != null && campaignOverlay.total > MONEY_TOL;

    if (campaignHasTotal && ldsTotalMissing) {
      total = campaignOverlay.total;
      totalSource = campaignOverlay.totalSource;
    }

    if (
      campaignOverlay.paid != null &&
      campaignOverlay.paid > MONEY_TOL &&
      ldsPaidMissing
    ) {
      paid = campaignOverlay.paid;
      paidSource = campaignOverlay.paidSource;
    }

    if (total != null && (remaining == null || (ldsTotalMissing && ldsPaidMissing))) {
      remaining = campaignOverlay.remaining;
      remainingSource = campaignOverlay.remainingSource;
    } else if (total != null && paid > MONEY_TOL) {
      remaining = round2(Math.max(total - paid, 0));
      if (remaining <= MONEY_TOL) {
        remainingSource = "derived:campaign_total_minus_paid";
      }
    }
  }

  if (total != null) {
    const handlingUplift = applyLdsMissingHandlingFeeToPayableTotal({
      total,
      order,
    });
    if (handlingUplift.addedHandlingFee && handlingUplift.total != null) {
      const previousTotal = total;
      total = handlingUplift.total;
      totalSource = `${totalSource || "backend"}${handlingUplift.sourceSuffix || " + handling_fee"}`;
      remaining = round2(Math.max(total - (paid || 0), 0));
      remainingSource = "derived:handling_fee_uplift_minus_paid";
      if (isDevRuntime()) {
        try {
          // eslint-disable-next-line no-console
          console.info("[LDS_HANDLING_FEE_PAYABLE_UPLIFT]", {
            orderId: String(order.id ?? order._id ?? order.orderid ?? ""),
            previousTotal,
            handlingFeeIls: handlingUplift.handlingFeeIls,
            total,
            remaining,
          });
        } catch {
          /* ignore */
        }
      }
    }
  }

  const hasBackendBlock = Boolean(
    (partial &&
      typeof partial === "object" &&
      (partial.display_total != null ||
        partial.display_paid != null ||
        partial.display_remaining != null)) ||
      updated?.display_total_updated != null
  );

  const out = {
    total,
    paid,
    remaining,
    totalSource,
    paidSource,
    remainingSource,
    hasBackendBlock,
    rejectedCandidates,
  };

  if (options.emitTrace !== false && isDevRuntime()) {
    try {
      // eslint-disable-next-line no-console
      console.info(LDS_BACKEND_FINANCIAL_SSOT_TRACE_TAG, {
        orderId: String(order.id ?? order._id ?? order.orderid ?? ""),
        backendTotal: out.total,
        backendPaid: out.paid,
        backendRemaining: out.remaining,
        totalSource: out.totalSource,
        paidSource: out.paidSource,
        remainingSource: out.remainingSource,
        hasBackendBlock: out.hasBackendBlock,
        rejectedCandidates: out.rejectedCandidates,
      });
    } catch {
      /* ignore */
    }
  }

  return out;
}

/**
 * @param {object|null|undefined} order
 * @param {{
 *   financial?: ReturnType<typeof resolveLdsBackendFinancialSsot>,
 *   displayedHeaderTotal?: number|null,
 *   displayedPaymentCardTotal?: number|null,
 *   displayedPaid?: number|null,
 *   displayedRemaining?: number|null,
 *   progressPercent?: number|null,
 * }} ctx
 */
export function buildLdsFeFinalFinancialSsotTracePayload(order, ctx = {}) {
  const fin =
    ctx.financial ??
    resolveLdsBackendFinancialSsot(order, { emitTrace: false });
  return {
    orderId: String(order?.id ?? order?._id ?? order?.orderid ?? ""),
    backendTotal: fin.total,
    backendPaid: fin.paid,
    backendRemaining: fin.remaining,
    displayedHeaderTotal: ctx.displayedHeaderTotal ?? fin.total,
    displayedPaymentCardTotal: ctx.displayedPaymentCardTotal ?? fin.total,
    displayedPaid: ctx.displayedPaid ?? fin.paid,
    displayedRemaining: ctx.displayedRemaining ?? fin.remaining,
    progressPercent: ctx.progressPercent ?? null,
    totalSource: fin.totalSource,
    rejectedCandidates: fin.rejectedCandidates,
  };
}

export function emitLdsFeFinalFinancialSsotTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info(LDS_FE_FINAL_FINANCIAL_SSOT_TRACE_TAG, payload);
  } catch {
    /* ignore */
  }
}
